import React, { Component, createRef, ReactNode } from "react";
import "./CardStack.css";

interface CardStackProps {
  children: ReactNode;
  height?: number;
  width?: string | number;
  headerHeight?: number;
  topHeight?: number;
  hoverOffset?: number;
  cardHeight?: number;
  stackDistanceBetweenCards?: number;
  selectedStackGap?: number;
  background?: string;
  initialCard?: number;
}

interface CardStackState {
  cardSelected: number;
  hoveredCard: number;
}

class CardStack extends Component<CardStackProps, CardStackState> {
  stackRef = createRef<HTMLDivElement>();

  static defaultProps = {
    width: "100%",
    height: 400,
    background: "transparent",
    hoverOffset: 0,
    cardHeight: 168,
    stackDistanceBetweenCards: 115,
    selectedStackGap: 80,
    initialCard: -1,
    headerHeight: 100,
    topHeight: 0,
  };

  constructor(props: CardStackProps) {
    super(props);
    this.state = {
      cardSelected: props.initialCard ?? -1,
      hoveredCard: -1,
    };
  }

  handleCardClick = (index: number) => {
    this.setState((prevState) => {
      const newSelected = prevState.cardSelected === index ? -1 : index;
      return {
        cardSelected: newSelected,
        hoveredCard: -1,
      };
    });
  };

  handleCardHover = (index: number, isHovering: boolean) => {
    if (this.state.cardSelected < 0) {
      this.setState({ hoveredCard: isHovering ? index : -1 });
    }
  };

  getCardStyle = (index: number, totalCards: number): React.CSSProperties => {
    const {
      topHeight = 0,
      hoverOffset = 25,
      height = 600,
      cardHeight = 168,
      stackDistanceBetweenCards = 115,
      selectedStackGap = 80,
    } = this.props;
    const { cardSelected, hoveredCard } = this.state;

    let transform = "none";
    let maxHeight: string;
    let zIndex = index; // Reversed: last card on top visually

    if (cardSelected >= 0) {
      // A card is selected
      if (index === cardSelected) {
        // Selected card always moves to the same position (card 0's position)
        const fixedOffset = topHeight;
        transform = `translate3d(0, ${fixedOffset}px, 0)`;
        maxHeight = `${height}px`;
        // Keep its original zIndex
      } else {
        // Stack cards directly below the selected card, starting with the requested gap.
        const selectedCardOffset = topHeight;
        const bottomStackStart = selectedCardOffset + cardHeight + selectedStackGap;

        // Calculate position in bottom stack
        // Cards before selected card keep their relative order (0, 1, 2...)
        // Cards after selected card continue the sequence
        let stackPosition;
        if (index < cardSelected) {
          stackPosition = index;
        } else {
          // index > cardSelected
          stackPosition = index - 1;
        }
        
        transform = `translate3d(0, ${bottomStackStart + (selectedStackGap * stackPosition)}px, 0)`;
        maxHeight = "none";
      }
    } else {
      // No card selected - stack view
      const baseOffset =
        index === 0
          ? topHeight
          : (stackDistanceBetweenCards * index) + topHeight;
      const hoverAdjustment = index === hoveredCard ? -hoverOffset : 0;
      transform = `translate3d(0, ${baseOffset + hoverAdjustment}px, 0)`;
      maxHeight = "none"; // Allow full content to show
    }


    return {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      width: "100%",
      transform,
      WebkitTransform: transform,
      maxHeight,
      zIndex,
      transition: "0.8s max-height ease, 0.8s transform ease",
      WebkitTransition: "0.8s max-height ease, -webkit-transform 0.8s ease",
      cursor: "pointer",
      overflow: "visible", // Allow box shadows to show
      boxSizing: "border-box", // Include padding in width calculation
    };
  };

  render() {
    const { background = "transparent", height = 600, width = "100%", children } = this.props;

    const childrenArray = React.Children.toArray(children);
    const totalCards = childrenArray.length;

    if (totalCards === 0) {
      return null;
    }

    const stackStyles: React.CSSProperties = {
      display: "flex",
      flexDirection: "column",
      position: "relative",
      background,
      height,
      width,
      overflow: "visible", // Always visible to show stacked cards at bottom
      listStyleType: "none",
      padding: 0,
      margin: 0,
    };

    return (
      <div ref={this.stackRef} style={stackStyles} className="card-stack">
        {childrenArray.map((child, index) => (
          <div
            key={index}
            style={this.getCardStyle(index, totalCards)}
            onClick={() => this.handleCardClick(index)}
            onMouseEnter={() => this.handleCardHover(index, true)}
            onMouseLeave={() => this.handleCardHover(index, false)}
            className="card-container"
          >
            {child}
          </div>
        ))}
      </div>
    );
  }
}

export default CardStack;
